<script setup lang="ts">
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useAgenticSearchStore } from '@/search/agentic_search.store'
import { vFocusOnMount } from '@/util/focus_on_mount'

const PROGRESS_STEPS = [
  'Searching through our catalog of 17,000+ assessments...',
  'Filtering by grade level and subject...',
  'Ranking results by relevance...'
]

defineProps<{ hasError: boolean }>()
const emit = defineEmits<{ restart: [] }>()

const { currentPhase } = storeToRefs(useAgenticSearchStore())

// Each phase the agent starts reveals one more step, so the list grows as the search runs.
const shownSteps = computed(() => PROGRESS_STEPS.slice(0, Number(currentPhase.value ?? 0) + 1))
</script>

<template>
  <div class="mx-auto flex max-w-[565.625px] flex-col items-center gap-6">
    <div class="w-full shrink-0 rounded-[8px] bg-gradient-to-r from-ai-purpleTealStart via-ai-purpleTealStart to-ai-purpleTealStop p-px">
      <div class="relative flex w-full flex-col gap-6 rounded-[7px] bg-gray-100 pb-[41px] pl-[41px] pr-[22px] pt-2">
        <button
          v-focus-on-mount
          type="button"
          class="-mb-2.5 flex h-10 items-center gap-1.5 self-end rounded-xs px-1 py-4 text-lg leading-4 text-blue-400 transition-colors hover:bg-blue-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-400"
          aria-label="Restart search"
          @click="emit('restart')"
        >
          <span class="material-icons-round text-display-sm leading-none" aria-hidden="true">replay</span>
          <span>Restart</span>
        </button>
        <div v-if="hasError" class="flex w-full max-w-[484px] items-center gap-2 rounded-xs bg-red-100 p-2">
          <span class="material-icons-round shrink-0 text-display-sm leading-6 text-gray-900" aria-hidden="true">warning_amber</span>
          <p class="m-0 text-md leading-5 text-gray-900" role="alert" aria-live="assertive">
            We're not able to retrieve assessments. Try again later.
          </p>
        </div>
        <template v-else>
          <div class="flex items-center gap-2.5">
            <div class="flex h-[26px] w-[26px] shrink-0 items-center justify-center">
              <span class="material-icons-round animate-spin text-display-sm leading-6 text-blue-400" aria-hidden="true">autorenew</span>
            </div>
            <p class="m-0 text-lg font-bold leading-6 text-gray-900" role="status" aria-live="polite">
              Searching 17,000+ assessments for the best matches...
            </p>
          </div>
          <div class="flex flex-col gap-2" role="status" aria-live="polite">
            <p v-for="step in shownSteps" :key="step" class="m-0 text-md leading-5 text-gray-700">
              {{ step }}
            </p>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>
